import { useMemo } from 'react'

import {
  useCubeReportAccessFiltersContext,
  useCubeReportPageFiltersContext,
} from 'cube'
import {
  CONVERSATION_FACTS_DIMENSIONS,
  CubeSchemaTables,
  DRIFT_CHAT_TAGS_DIMENSIONS,
} from 'cube/constants/QueryConstants'

import {
  CubeQueryBuilder,
  resultSetAsTableData,
  useCubeQuery,
} from '@driftt/dds.reports'

import useTimePeriod from 'hooks/useTimePeriod'

import { ConversationTagsBaseQuery } from './ConversationTagsBaseQuery.query'

export type ConversationTagsMetricRow = {
  tagName: string
  conversationCount: number
}

export type ConversationTagsMetric = {
  [CubeSchemaTables.CONVERSATION_TAGS]: {
    name: string
  }
  [CubeSchemaTables.CONVERSATIONS]: {
    count: number
  }
}

export interface ConversationsStartedFromResult {
  tableData: ConversationTagsMetricRow[]
  isLoadingTableData: boolean
  isErrorFetchingTableData: boolean
}

/***
 * Hook that uses cube to fetch conversations started from data.
 *
 * @returns {ConversationsStartedFromResult}
 */
export const useConversationTags = () => {
  const timePeriod = useTimePeriod()
  const { pageFilters } = useCubeReportPageFiltersContext()
  const { accessFilters } = useCubeReportAccessFiltersContext()

  const conversationsCreatedFromUrlCubeQuery = new CubeQueryBuilder({
    query: ConversationTagsBaseQuery,
  })
    .withFilters([
      {
        member: DRIFT_CHAT_TAGS_DIMENSIONS.TAG_NAME,
        operator: 'set',
      },
      ...pageFilters,
      ...accessFilters,
    ])
    .withinDateRange(
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
      timePeriod.startDate.toDate(),
      timePeriod.endDate.toDate()
    )
    .withLimit(10)
    .build()

  const {
    data,
    isLoading: isLoadingTableData,
    isError: isErrorFetchingTableData,
  } = useCubeQuery({
    query: conversationsCreatedFromUrlCubeQuery,
  })

  const tableData: ConversationTagsMetricRow[] = useMemo(() => {
    const tableRows = resultSetAsTableData(data) as ConversationTagsMetric[]

    return tableRows.map((row) => {
      return {
        tagName: row[CubeSchemaTables.CONVERSATION_TAGS].name,
        conversationCount: row[CubeSchemaTables.CONVERSATIONS].count,
      }
    })
  }, [data])

  return useMemo(
    () => ({ tableData, isLoadingTableData, isErrorFetchingTableData }),
    [tableData, isLoadingTableData, isErrorFetchingTableData]
  )
}

export default useConversationTags
